import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { API } from "@/App";
import axios from "axios";
import { Clock, ChevronLeft, ChevronRight, Maximize2, Minimize2, X } from "lucide-react";

// Simple, reliable video player using native browser controls
// Always displays in 16:9 aspect ratio (YouTube-style) on desktop
const ClipPlayer = ({ clip, isActive, onEnded, isLandscapeMode }) => {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const sideColor = clip.side === "red" ? "border-red-500" : "border-blue-500";

  return (
    <div className={`relative rounded-lg overflow-hidden border-2 ${sideColor} ${!isActive ? 'opacity-50' : ''}`}>
      {/* Header - Compact in landscape mode */}
      <div className={`px-3 py-1.5 ${isLandscapeMode ? 'py-1' : 'py-2'} ${clip.side === "red" ? "bg-red-500/20" : "bg-blue-500/20"}`}>
        <div className="flex items-center gap-2">
          <span className={isLandscapeMode ? "text-sm" : "text-lg"}>{clip.side === "red" ? "🔴" : "🔵"}</span>
          <span className={`font-medium ${isLandscapeMode ? 'text-xs' : 'text-sm'}`}>{clip.debater_username}</span>
          <Badge variant="outline" className={`${isLandscapeMode ? 'text-[10px] px-1.5 py-0' : 'text-xs'} ml-auto`}>{clip.round_name}</Badge>
        </div>
      </div>

      {/* Loading state */}
      {loading && !error && (
        <div className="absolute inset-0 top-12 flex items-center justify-center bg-black z-10">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-white mx-auto mb-2" />
            <p className="text-xs text-muted-foreground">Loading video...</p>
          </div>
        </div>
      )}

      {/* Video container - Always 16:9 aspect ratio like YouTube */}
      <div className="relative w-full bg-black" style={{ aspectRatio: '16/9' }}>
        <video
          src={clip.video_url}
          className="absolute inset-0 w-full h-full object-contain bg-black"
          style={{
            transform: 'translateZ(0)',
            willChange: 'transform',
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden'
          }}
          controls
          playsInline
          preload="auto"
          onLoadStart={() => setLoading(true)}
          onLoadedData={() => setLoading(false)}
          onError={() => { setError("Video failed to load"); setLoading(false); }}
          onCanPlay={() => { setError(null); }}
          onEnded={onEnded}
        />
      </div>

      {/* Error state */}
      {error && (
        <div className="absolute inset-0 top-12 flex items-center justify-center bg-black/80">
          <div className="text-center p-4">
            <p className="text-red-400 text-sm mb-2">{error}</p>
            <a 
              href={clip.video_url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-400 underline text-sm"
            >
              Open video directly
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

// Main Debate Playback Component
const DebatePlayback = ({ debateId, onClose }) => {
  const [playbackData, setPlaybackData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentClipIndex, setCurrentClipIndex] = useState(0);
  const [isLandscape, setIsLandscape] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect orientation and mobile
  useEffect(() => {
    const checkOrientation = () => {
      const isLandscapeMode = window.matchMedia('(orientation: landscape)').matches;
      const isMobileDevice = window.innerWidth < 1024;
      setIsLandscape(isLandscapeMode);
      setIsMobile(isMobileDevice);
    };
    
    checkOrientation();
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);
    
    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, []);

  useEffect(() => {
    const fetchPlayback = async () => {
      try {
        const res = await axios.get(`${API}/debates/${debateId}/playback`);
        setPlaybackData(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || "Failed to load playback");
      } finally {
        setLoading(false);
      }
    };
    fetchPlayback();
  }, [debateId]);

  const handleClipEnded = () => {
    // Auto-advance to next clip
    if (playbackData?.clips && currentClipIndex < playbackData.clips.length - 1) {
      setCurrentClipIndex(prev => prev + 1);
    }
  };

  const goToNextClip = () => {
    if (playbackData?.clips && currentClipIndex < playbackData.clips.length - 1) {
      setCurrentClipIndex(prev => prev + 1);
    }
  };

  const goToPrevClip = () => {
    if (currentClipIndex > 0) {
      setCurrentClipIndex(prev => prev - 1);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  if (!playbackData?.playback_available) {
    return (
      <div className="text-center py-8">
        <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
        <h3 className="font-bold mb-2">Playback Not Available Yet</h3>
        <p className="text-muted-foreground text-sm">
          Full debate replay will be available once all rounds are completed.
        </p>
      </div>
    );
  }

  const currentClip = playbackData.clips[currentClipIndex];
  const statusBanner = playbackData.status_banner;
  const isLandscapeMobile = isLandscape && isMobile;

  // Landscape mode on mobile - optimized fullscreen-like view
  if (isLandscapeMobile) {
    return (
      <div className="fixed inset-0 z-50 bg-black flex flex-col">
        {/* Compact header bar */}
        <div className="flex items-center justify-between px-3 py-1.5 bg-black/90">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{playbackData.title}</span>
            <span className="text-xs text-muted-foreground">•</span>
            <span className="text-xs font-medium">{currentClip?.round_name}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {currentClipIndex + 1}/{playbackData.clips.length}
            </span>
          </div>
        </div>

        {/* Main video - takes most of the screen */}
        <div className="flex-1 relative">
          {currentClip && (
            <div className="absolute inset-0 flex items-center justify-center bg-black">
              <div className="relative w-full h-full max-h-full" style={{ aspectRatio: '16/9' }}>
                <video
                  src={currentClip.video_url}
                  className="absolute inset-0 w-full h-full object-contain bg-black"
                  style={{
                    transform: 'translateZ(0)',
                    willChange: 'transform',
                    backfaceVisibility: 'hidden',
                    WebkitBackfaceVisibility: 'hidden'
                  }}
                  controls
                  playsInline
                  autoPlay
                  onEnded={handleClipEnded}
                />
                {/* Corner indicator */}
                <div className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                  currentClip.side === "red" ? "bg-red-500/80" : "bg-blue-500/80"
                }`}>
                  {currentClip.side === "red" ? "🔴" : "🔵"} {currentClip.debater_username}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Compact navigation bar */}
        <div className="flex items-center justify-between px-3 py-2 bg-black/90">
          <Button 
            variant="ghost" 
            size="sm"
            disabled={currentClipIndex === 0}
            onClick={goToPrevClip}
            className="h-8 px-2"
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="text-xs ml-1">Prev</span>
          </Button>
          
          {/* Clip dots */}
          <div className="flex items-center gap-1.5">
            {playbackData.clips.map((clip, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentClipIndex(idx)}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  idx === currentClipIndex 
                    ? (clip.side === "red" ? "bg-red-500 scale-125" : "bg-blue-500 scale-125")
                    : (clip.side === "red" ? "bg-red-500/40" : "bg-blue-500/40")
                }`}
              />
            ))}
          </div>
          
          <Button 
            variant="ghost" 
            size="sm"
            disabled={currentClipIndex >= playbackData.clips.length - 1}
            onClick={goToNextClip}
            className="h-8 px-2"
          >
            <span className="text-xs mr-1">Next</span>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  }

  // Default portrait/desktop view
  return (
    <div className="space-y-4">
      {/* Status Banner for Early Endings */}
      {statusBanner && statusBanner.type !== "completed" && (
        <div className={`p-3 rounded-lg border ${
          statusBanner.type === "concession" ? "bg-yellow-500/10 border-yellow-500/30" :
          "bg-red-500/10 border-red-500/30"
        }`}>
          <p className={`text-sm font-medium ${
            statusBanner.type === "concession" ? "text-yellow-400" : "text-red-400"
          }`}>
            {statusBanner.message}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {playbackData.rounds_completed} of 3 rounds completed
          </p>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">{playbackData.title}</h2>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="outline">{playbackData.category}</Badge>
            <span className="text-sm text-muted-foreground">
              {playbackData.total_clips} clips
            </span>
          </div>
        </div>
        {onClose && (
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        )}
      </div>

      {/* Debaters */}
      <div className="flex items-center justify-between bg-card/30 rounded-lg p-3">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-red-500 rounded-full" />
          <Avatar className="w-8 h-8">
            <AvatarImage src={playbackData.red_corner?.picture} />
            <AvatarFallback>{playbackData.red_corner?.username?.[0]}</AvatarFallback>
          </Avatar>
          <span className="font-medium">{playbackData.red_corner?.username}</span>
        </div>
        <span className="text-muted-foreground font-bold">VS</span>
        <div className="flex items-center gap-2">
          <span className="font-medium">{playbackData.blue_corner?.username}</span>
          <Avatar className="w-8 h-8">
            <AvatarImage src={playbackData.blue_corner?.picture} />
            <AvatarFallback>{playbackData.blue_corner?.username?.[0]}</AvatarFallback>
          </Avatar>
          <div className="w-3 h-3 bg-blue-500 rounded-full" />
        </div>
      </div>

      {/* Main Video Player */}
      {currentClip && (
        <ClipPlayer 
          clip={currentClip}
          isActive={true}
          onEnded={handleClipEnded}
          isLandscapeMode={isLandscapeMobile}
        />
      )}

      {/* Simple Navigation */}
      <div className="flex items-center justify-between bg-card/30 rounded-lg p-3">
        <Button 
          variant="ghost" 
          size="sm"
          disabled={currentClipIndex === 0}
          onClick={goToPrevClip}
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Previous
        </Button>
        
        <div className="text-center">
          <div className="text-sm font-medium">
            {currentClip?.round_name} - {currentClip?.side === "red" ? "🔴 Red" : "🔵 Blue"}
          </div>
          <div className="text-xs text-muted-foreground">
            Clip {currentClipIndex + 1} of {playbackData.clips.length}
          </div>
        </div>
        
        <Button 
          variant="ghost" 
          size="sm"
          disabled={currentClipIndex >= playbackData.clips.length - 1}
          onClick={goToNextClip}
        >
          Next
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>

      {/* Clip Timeline */}
      <div className="space-y-2">
        <div className="text-sm font-medium">All Clips</div>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
          {playbackData.clips.map((clip, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentClipIndex(idx)}
              className={`p-2 rounded text-xs text-center transition-all ${
                idx === currentClipIndex 
                  ? 'bg-primary text-primary-foreground' 
                  : 'bg-card/30 hover:bg-card/50'
              }`}
            >
              <div className={`w-2 h-2 rounded-full mx-auto mb-1 ${clip.side === "red" ? "bg-red-500" : "bg-blue-500"}`} />
              <div className="truncate">{clip.round_name}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DebatePlayback;
